// src/features/hoa-admin/screens/CommunicationsPage.tsx
import React from "react";
import { useSearchParams } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

// Re-use the existing standalone screens (no rewrites)
import AnnouncementsPage from "./AnnouncementsPage";
import PollingPage from "./PollingPage";
import IssuesPage from "./IssuesPage";

const PRIMARY = "#1F3A93";
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

type TabKey = "announcements" | "polling" | "issues"; // issues = Reported Issues label

export default function CommunicationsPage() {
  const [params, setParams] = useSearchParams();
  const active: TabKey = (params.get("tab") as TabKey) || "announcements";
  const { t } = useLanguage();

  function setTab(tab: TabKey) {
    params.set("tab", tab);
    setParams(params, { replace: true });
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-[18px] font-semibold text-gray-900">
          {t("hoa.communications.title")}
        </h1>
        <p className="text-sm text-gray-600">
          {t("hoa.communications.subtitle")}
        </p>
      </div>

      {/* Tabs (same quiet style you’re using across the app) */}
      <Card variant="glass" className="rounded-3xl p-2.5 sm:p-3">
        <div className="flex flex-wrap items-center gap-2">
          <TabButton
            label={t("hoa.communications.tabs.announcements")}
            active={active === "announcements"}
            onClick={() => setTab("announcements")}
          />
          <TabButton
            label={t("hoa.communications.tabs.polling")}
            active={active === "polling"}
            onClick={() => setTab("polling")}
          />
          <TabButton
            label={t("hoa.communications.tabs.issues")}
            active={active === "issues"}
            onClick={() => setTab("issues")}
          />
        </div>
      </Card>

      {/* Content */}
      <div className="space-y-4">
        {active === "announcements" && <AnnouncementsPage />}
        {active === "polling" && <PollingPage />}
        {active === "issues" && <IssuesPage />}
      </div>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={[
        "h-9 px-3 text-sm font-medium transition",
        QUIET,
        active ? "!bg-gray-50" : "",
      ].join(" ")}
      style={{
        borderColor: active ? "rgba(0,0,0,0.12)" : undefined,
        boxShadow: active ? "inset 0 0 0 1px rgba(0,0,0,0.04)" : undefined,
        color: "#111827",
      }}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
 
